
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { ParamList } from '@/collections/params';
import { ParamModel } from '@/models/param';
import { MeasureModel } from '@/models/measure';
import { MeasureList } from '@/collections/measures';
import { StoreModel } from '@/models/store';
import ReportParam from '@/components/ReportParam.vue';
import DateFilter from '@/components/DateFilter.vue';
import { ReorderUserParamsService } from '@/services/reorderUserParams';
import { buildPresetReportParams } from '@/helpers/reorderPresetParams';
import { shouldApplyDefaultReorderSettings, canPersistReorderToDatabase } from '@/components/reorderUserParamsHelpers';

interface InventoryPresetConfig {
  [key: string]: Array<{code: string, value: string[]}>;
}

@Component({
  components: {
    ReportParam,
    DateFilter,
  },
})
export default class ReorderUserParams extends Vue {
  @Prop({ required: true })
  public measures!: MeasureList;

  @Prop({ required: true })
  public store!: StoreModel;

  @Prop({ default: () => new ParamList() })
  public value!: ParamList;

  @Prop({ default: true })
  public showGuide!: boolean;

  @Prop({ default: true })
  public showPresets!: boolean;

  @Prop({ default: false })
  public initialExpanded!: boolean;

  @Prop({ default: true })
  public defaultShowNeedReorderNow!: boolean;

  @Prop({ default: true })
  public showFilterOptions!: boolean;

  @Prop({ default: 'database' })
  public persistenceMode!: 'localStorage' | 'database' | 'hybrid';

  @Prop({ default: true })
  public autoSave!: boolean;

  @Prop({ default: 2000 })
  public saveDebounceMs!: number;

  // Internal state
  private expanded: boolean = false;
  private loading: boolean = false;
  private inventoryMeasure: MeasureModel = new MeasureModel();
  private inventoryMeasureParams: ParamList = new ParamList();
  private reportParams: ParamList = new ParamList();
  private initReportParams: ParamList = new ParamList();
  private paramKey: number = 0; // Force re-render counter
  
  // Date range properties
  private dateRange: string[] = [];
  private defaultDateRange: string = '3m';
  private hasRestoredSettings: boolean = false;
  private isComponentReady: boolean = false;
  
  // Show only products that need reordering now
  private showNeedReorderNowOnly: boolean = false;
  
  // Apply button functionality
  public hasPendingChanges: boolean = false;
  
  // Flag to track if we should apply default preset after measure loads
  private shouldApplyDefaultPreset: boolean = false;
  
  // Flag to track when we're restoring from localStorage (not user changes)
  private isRestoring: boolean = false;
  
  // Local storage key for user parameters
  private readonly STORAGE_KEY = 'reorderUserParams';
  
  // Database persistence properties
  private isDatabaseLoading: boolean = false;
  private isSavingToDatabase: boolean = false;
  private databaseSaveError: string | null = null;
  private saveDebounceTimer: any = null;
  private hasLoadedFromDatabase: boolean = false;
  // True when a database read threw (network/HTTP error) — distinct from a
  // successful "no saved settings" response. Guards against applying/persisting
  // defaults over settings we simply could not read (CodeRabbit MR !2738).
  private databaseLoadFailed: boolean = false;
  

  // Inventory-specific presets
  private inventoryPresets: InventoryPresetConfig = {
    conservative: [
      { code: 'sales_trend', value: ['0.9'] }, // -10% trend
      { code: 'topn', value: ['50'] }
    ],
    balanced: [
      { code: 'sales_trend', value: ['1.0'] }, // Normal trend
      { code: 'topn', value: ['All'] }
    ],
    aggressive: [
      { code: 'sales_trend', value: ['1.2'] }, // +20% trend
      { code: 'topn', value: ['All'] }
    ],
    seasonal: [
      { code: 'sales_trend', value: ['1.3'] }, // +30% trend for seasonal boost
      { code: 'topn', value: ['All'] }
    ]
  };

  get hasActiveParams(): boolean {
    return this.reportParams.size() > 0;
  }

  get activeParamsCount(): number {
    let count = this.reportParams.size();
    if (this.dateRange.length >= 2) count++;
    if (!this.showNeedReorderNowOnly) count++; // Count as active when disabled (showing all products)
    return count;
  }

  get computedInitRange(): string {
    // If we have restored date range, use 'custom' so DateFilter knows it's custom dates
    if (this.hasRestoredSettings && this.dateRange.length >= 2) {
      return 'custom';
    }
    // Otherwise use default preset
    return this.defaultDateRange;
  }

  // Detect which preset is currently active based on parameters
  get activePreset(): string | null {
    if (this.reportParams.size() === 0) return null;
    
    // Check each preset to see if current parameters match
    for (const [presetName, presetConfig] of Object.entries(this.inventoryPresets)) {
      let matches = true;
      
      // Check if all preset parameters match current reportParams
      for (const presetParam of presetConfig) {
        const currentParam = this.reportParams.items.find(p => p.code === presetParam.code);
        if (!currentParam ||
            !Array.isArray(currentParam.value) ||
            currentParam.value.length !== presetParam.value.length ||
            !currentParam.value.every((val: string, idx: number) => val === presetParam.value[idx])) {
          matches = false;
          break;
        }
      }
      
      // A preset is defined only by the params it carries (sales_trend, topn);
      // the full report set legitimately contains other measure params, so an
      // exact size match would wrongly classify every applied preset as custom.
      if (matches) {
        return presetName;
      }
    }
    
    return 'custom'; // Parameters exist but don't match any preset
  }

  // Get description for active preset
  get activePresetDescription(): string {
    const preset = this.activePreset;
    if (!preset || preset === 'custom') return '';
    
    const descriptions: { [key: string]: string } = {
      conservative: 'Risk-averse ordering with -10% sales trend and top 50 products only. Best for limited capital and proven products.',
      balanced: 'Standard reordering with normal sales trend and all products. Best for stable business conditions.',
      aggressive: 'Growth-oriented ordering with +20% sales trend and all products. Best for marketing campaigns and high confidence periods.',
      seasonal: 'Year-over-year seasonal planning with +30% sales trend comparing to next 90 days last year. Best for holiday preparation.'
    };
    
    return descriptions[preset] || '';
  }


  async mounted() {
    this.isRestoring = true; // Start restoration mode for entire initialization
    
    // Load from database first if using database or hybrid mode
    if (this.persistenceMode === 'database' || this.persistenceMode === 'hybrid') {
      await this.loadFromDatabase();
    }
    
    // Load from localStorage as fallback or primary (depending on mode)
    if (this.persistenceMode === 'localStorage' || 
        (this.persistenceMode === 'hybrid' && !this.hasLoadedFromDatabase)) {
      this.loadFromLocalStorage();
    }
    
    // Then apply any props that should override saved settings
    // Always default to closed unless explicitly set to true
    this.expanded = this.initialExpanded === true;
    
    // Apply default props ONLY for a brand-new user with no saved settings in
    // either source. In 'database' mode nothing is written to localStorage, so
    // guarding on localStorage alone would clobber the settings just restored
    // from the database (saved reorder preferences never reaching the table).
    if (shouldApplyDefaultReorderSettings(
      this.hasLoadedFromDatabase,
      !!localStorage.getItem(this.STORAGE_KEY),
      this.databaseLoadFailed,
    )) {
      this.reportParams = this.value;
      this.showNeedReorderNowOnly = this.defaultShowNeedReorderNow;
      // Apply balanced preset as default
      this.applyDefaultBalancedPreset();
    }
    
    this.hasPendingChanges = false; // Start with no pending changes
    
    this.initInventoryMeasure();
    
    // Delay showing DateFilter to ensure our dateRange is properly set
    setTimeout(() => {
      this.isComponentReady = true;
      this.$nextTick(() => {
        this.setRestoredDateRange();
        // Set default date range if this is a new user (no saved settings)
        if (this.shouldApplyDefaultPreset && this.dateRange.length === 0) {
          this.setPresetDateRange('3m');
        }
        
        // Sync parameters again after components are ready
        this.syncParametersWithMeasure();
        
        // End restoration mode after all initialization is complete
        this.$nextTick(() => {
          this.isRestoring = false;
          this.hasPendingChanges = false; // Ensure no pending changes after full initialization
          
          // Final parameter sync to ensure form elements have latest values
          this.$nextTick(() => {
            this.syncParametersWithMeasure();
            
            // Emit that params are loaded and ready
            this.$emit('params-loaded', {
              reportParams: this.reportParams,
              dateRange: this.dateRange,
              showNeedReorderNowOnly: this.showNeedReorderNowOnly
            });
          });
        });
      });
    }, 100); // Small delay to ensure everything is initialized
  }

  @Watch('measures', { immediate: true })
  onMeasuresChange() {
    if (this.measures && this.measures.items.length > 0) {
      this.initInventoryMeasure();
    }
  }

  @Watch('value', { immediate: true, deep: true })
  onValueChange(_newValue: ParamList, oldValue: ParamList) {
    // Only update if this is the initial load (oldValue is undefined) 
    // or if value is being set from outside the component
    if (!oldValue) {
      this.reportParams = this.value;
    }
  }

  @Watch('hasRestoredSettings')
  onRestoredSettingsChange() {
    // Force re-render when restored settings flag changes
    this.paramKey++;
  }

  private async initInventoryMeasure() {
    this.loading = true;
    try {
      const measureCode = 'inventory_replenishment';
      
      // Find inventory_replenishment measure
      if (this.measures.items.length === 0) {
        this.inventoryMeasure.code = measureCode;
        await this.inventoryMeasure.fetch();
      } else {
        for (const item of this.measures.items) {
          if (item.code === measureCode) {
            this.inventoryMeasure = item;
            break;
          }
        }
      }

      // Extract parameters from the measure
      this.inventoryMeasureParams = new ParamList();
      if (this.inventoryMeasure.params && this.inventoryMeasure.params.size() > 0) {
        for (const item of this.inventoryMeasure.params.items) {
          this.inventoryMeasureParams.add(item);
        }
      }

      // Apply default balanced preset if needed
      if (this.shouldApplyDefaultPreset) {
        this.applyDefaultPresetParameters();
        this.shouldApplyDefaultPreset = false;
      }
      
      // Sync loaded parameters with measure definitions
      this.syncParametersWithMeasure();

    } catch (error) {
      console.error('Failed to load inventory_replenishment measure:', error);
    } finally {
      this.loading = false;
    }
  }


  private onParamsChange(params: ParamList) {
    this.reportParams = params;
    if (!this.isRestoring) {
      this.hasPendingChanges = true;
    }
    this.$emit('input', params);
    this.$emit('change', params);
    // No auto-save - wait for Apply button
  }

  private onRefreshNeeded() {
    // Mark as pending change instead of immediately refreshing
    this.hasPendingChanges = true;
    // Don't emit refresh-needed immediately - wait for apply button
  }

  private onDateRangeChange(dates: string[]) {
    this.dateRange = dates;
    if (!this.isRestoring) {
      this.hasPendingChanges = true;
    }
    // Emit date range changes so parent component can handle them
    this.$emit('date-range-changed', dates);
    // Don't trigger refresh immediately - wait for apply button
    // No auto-save - wait for Apply button
  }

  private onShowNeedReorderNowChange() {
    if (!this.isRestoring) {
      this.hasPendingChanges = true;
    }
    // Emit the change so parent component can handle it
    this.$emit('show-need-reorder-now-changed', this.showNeedReorderNowOnly);
    // Don't trigger refresh immediately - wait for apply button
    // No auto-save - wait for Apply button
  }


  private applyInventoryPreset(presetName: string) {
    const preset = this.inventoryPresets[presetName];
    if (!preset) {
      return;
    }

    // Set showNeedReorderNowOnly to false for all presets
    this.showNeedReorderNowOnly = false;

    // Set historical date range based on preset
    if (presetName === 'seasonal') {
      // Seasonal preset uses "next 90 days last year"
      this.setPresetDateRange('next-90d-last-year');
    } else {
      // All other presets use "last 3 months"
      this.setPresetDateRange('3m');
    }

    // Rebuild the FULL param set: every measure param the reorder calculation
    // needs, with the preset's params overridden and the user's other configured
    // params preserved. Previously this only kept the preset's own params
    // (sales_trend, topn), silently dropping the rest.
    const currentParams = this.reportParams;
    this.reportParams = buildPresetReportParams(this.inventoryMeasureParams, currentParams, preset);
    this.initReportParams = buildPresetReportParams(this.inventoryMeasureParams, currentParams, preset);

    this.paramKey++; // Force re-render
    this.onParamsChange(this.reportParams);
    
    // Don't apply immediately - let user click Apply button
    
  }

  private async applyChanges() {
    // Await the save so we don't report success when persistence is blocked
    // (guarded during a database read / after a failed read). Otherwise the UI
    // clears the pending flag and reports "applied" for a change that never
    // reached the database — the db-mode localStorage fallback is never reloaded.
    const saved = await this.saveSettingsImmediately();

    // Keep the change pending when persistence is blocked/failed so the user can
    // retry. The database-save-error event is emitted by performDatabaseSave (the
    // single source), so we don't re-emit it here — that would duplicate the
    // notification with an inconsistent payload.
    if (saved) {
      this.hasPendingChanges = false;
    }

    // Refresh regardless: the parameters are applied in-memory, so the parent
    // should reload the table to reflect them even if persistence is deferred.
    this.$emit('refresh-needed');
  }

  // Public methods for external control
  public async resetParams() {
    // First, clear all values in the UI immediately for visual feedback
    this.reportParams = new ParamList();
    this.initReportParams = new ParamList();
    this.dateRange = [];
    this.showNeedReorderNowOnly = false;
    // Mark as having pending changes - user needs to click Apply to save the reset
    this.hasPendingChanges = true;
    
   // Now set default values
    if (this.inventoryMeasureParams && this.inventoryMeasureParams.size() > 0) {
      const newReportParams = new ParamList();
      const newInitReportParams = new ParamList();
      
      for (const measureParam of this.inventoryMeasureParams.items) {
        // For sales_trend, explicitly set to 1.0 (0% adjustment)
        let defaultValue = measureParam.default || [];
        if (measureParam.code === 'sales_trend') {
          defaultValue = ['1'];
        } else if (measureParam.code === 'topn') {
          defaultValue = ['All'];
        }
        
        const param = new ParamModel({
          code: measureParam.code,
          name: measureParam.name,
          value: Array.isArray(defaultValue) ? [...defaultValue] : [defaultValue],
          valueType: measureParam.valueType,
          dataType: measureParam.dataType,
          listValues: measureParam.listValues,
          default: measureParam.default
        });
        newReportParams.add(param);
        
        // Create a copy for initReportParams
        const initParam = new ParamModel({
          code: measureParam.code,
          name: measureParam.name,
          value: Array.isArray(defaultValue) ? [...defaultValue] : [defaultValue],
          valueType: measureParam.valueType,
          dataType: measureParam.dataType,
          listValues: measureParam.listValues,
          default: measureParam.default
        });
        newInitReportParams.add(initParam);
      }
      
      this.reportParams = newReportParams;
      this.initReportParams = newInitReportParams;
    }
        
    // Force component re-render to show empty dropdowns
    this.paramKey++;
    
    // Wait for DOM update to ensure dropdowns are cleared
    await this.$nextTick();
    
    // Small delay for better visual feedback
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Don't clear saved settings yet - user needs to click Apply to persist the reset
    
    // Force another re-render with new values
    this.paramKey++;
    
    // Reset date filter component to 3 months
    await this.$nextTick();
    this.setPresetDateRange('3m');
    
    // Emit params change (but don't save)
    this.onParamsChange(this.reportParams);
    
    // Keep pending changes true - user needs to click Apply
    this.hasPendingChanges = true;
    
    // Emit reset event and date range change
    this.$emit('reset');
    this.$emit('date-range-changed', this.dateRange);
  }

  public clearSavedSettings() {
    this.clearLocalStorage();
  }

  public async revertChanges() {
    // Revert to last saved state
    this.isRestoring = true;
    
    try {
      // Reload settings from storage
      if (this.persistenceMode === 'database') {
        await this.loadFromDatabase();
      } else {
        this.loadFromLocalStorage();
      }
      
      // Clear pending changes flag
      this.hasPendingChanges = false;
      
      // Force re-render
      this.paramKey++;
      
      // Emit events to notify parent
      this.$emit('changes-reverted');
    } finally {
      this.isRestoring = false;
    }
  }

  public expandPanel() {
    this.expanded = true;
    // No auto-save - only save when Apply is clicked
  }

  public collapsePanel() {
    this.expanded = false;
    // No auto-save - only save when Apply is clicked
  }

  public applyPreset(presetName: string) {
    this.applyInventoryPreset(presetName);
  }

  private toggleExpanded() {
    this.expanded = !this.expanded;
    // No auto-save - only save when Apply is clicked
  }

  // Format date range for collapsed summary display
  private formatDateRangeSummary(): string {
    if (this.dateRange.length < 2) return '';
    
    // Check if we have valid dates in positions [0] and [1]
    const startDateStr = this.dateRange[0];
    const endDateStr = this.dateRange[1];
    
    if (!startDateStr || !endDateStr || startDateStr === '' || endDateStr === '') {
      // If no valid dates, check if we have a preset string in position [2]
      if (this.dateRange.length === 3 && this.dateRange[2]) {
        return this.formatPresetLabel(this.dateRange[2]);
      }
      return '';
    }
    
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);
    
    // Validate dates are valid
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      // Fallback to preset label if dates are invalid
      if (this.dateRange.length === 3 && this.dateRange[2]) {
        return this.formatPresetLabel(this.dateRange[2]);
      }
      return '';
    }
    
    // Format dates to short format
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: startDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
      });
    };
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  }

  private formatPresetLabel(preset: string): string {
    const presetLabels: { [key: string]: string } = {
      'next-90d-last-year': 'Next 90 days last year',
      '3m': 'Last 3 months',
      '6m': 'Last 6 months',
      '1y': 'Last year',
      '2y': 'Last 2 years',
      'ytd': 'Year to date',
      'last-30d': 'Last 30 days',
      'last-90d': 'Last 90 days',
      'last-6m': 'Last 6 months',
      'last-1y': 'Last year',
      'custom': 'Custom range'
    };
    
    return presetLabels[preset] || preset;
  }

  // Format detailed parameter summary for collapsed display
  private getParameterSummaries(): Array<{label: string, value: string, icon: string}> {
    const summaries: Array<{label: string, value: string, icon: string}> = [];
    
    // Add each report parameter with its current value
    for (const param of this.reportParams.items) {
      if (!['sales_trend'].includes(param.code)) {
        continue;
      }
      let displayValue = '';
      
      if (param.value && Array.isArray(param.value)) {
        if (param.value.length === 1) {
          displayValue = param.value[0];
        } else if (param.value.length > 1) {
          displayValue = `${param.value.length} items`;
        }
      } else if (param.value) {
        displayValue = String(param.value);
      }
      
      // Format specific parameter types
      if (param.code === 'sales_trend') {
        const trendValue = parseFloat(displayValue);
        if (!isNaN(trendValue)) {
          const percentage = ((trendValue - 1) * 100).toFixed(0);
          displayValue = `${Number(percentage) > 0 ? '+' : ''}${percentage}%`;
        }
      } else if (param.code === 'topn') {
        if (displayValue === 'All') {
          displayValue = 'All products';
        } else {
          displayValue = `Top ${displayValue}`;
        }
      }
      
      summaries.push({
        label: param.name || param.code,
        value: displayValue,
        icon: this.getParameterIcon(param.code)
      });
    }
    
    return summaries;
  }

  // Get appropriate icon for parameter type
  private getParameterIcon(paramCode: string): string {
    const icons: { [key: string]: string } = {
      'sales_trend': 'mdi-trending-up',
      'topn': 'mdi-format-list-numbered',
      'category': 'mdi-tag-multiple',
      'supplier': 'mdi-account-tie',
      'location': 'mdi-map-marker',
      'brand': 'mdi-crown',
      'price_range': 'mdi-currency-usd'
    };
    
    return icons[paramCode] || 'mdi-tune';
  }

  // Save current settings to local storage
  private saveToLocalStorage() {
    try {
      const settings = {
        dateRange: this.dateRange,
        showNeedReorderNowOnly: this.showNeedReorderNowOnly,
        reportParams: this.reportParams.items.map(param => ({
          code: param.code,
          name: param.name,
          value: param.value,
          valueType: param.valueType,
          dataType: param.dataType
        })),
        expanded: this.expanded,
        timestamp: new Date().getTime()
      };
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to save settings to local storage:', error);
    }
  }

  // Load settings from local storage
  private loadFromLocalStorage() {
    try {
      const savedSettings = localStorage.getItem(this.STORAGE_KEY);
      if (!savedSettings) return;

      const settings = JSON.parse(savedSettings);

      // Restore date range
      if (settings.dateRange && Array.isArray(settings.dateRange)) {
        this.dateRange = settings.dateRange;
        this.hasRestoredSettings = true;
      }

      // Restore filter option
      if (typeof settings.showNeedReorderNowOnly === 'boolean') {
        this.showNeedReorderNowOnly = settings.showNeedReorderNowOnly;
      }

      // Restore expanded state - default to closed (false)
      if (typeof settings.expanded === 'boolean') {
        this.expanded = false; // Always start closed by default
      }

      // Restore report parameters
      if (settings.reportParams && Array.isArray(settings.reportParams)) {
        const restoredParams = new ParamList();
        const initParams = new ParamList();
        
        settings.reportParams.forEach((paramData: any) => {
          // Normalize value to always be an array — see applyDatabaseSettings
          // for the underlying corruption path.
          const normalizedValue = Array.isArray(paramData.value)
            ? paramData.value
            : (paramData.value === null || paramData.value === undefined ? [] : [paramData.value]);

          // Create param for reportParams
          const param = new ParamModel({
            code: paramData.code,
            name: paramData.name,
            value: [...normalizedValue],
            valueType: paramData.valueType,
            dataType: paramData.dataType
          });
          restoredParams.add(param);

          // Create a copy for initReportParams
          const initParam = new ParamModel({
            code: paramData.code,
            name: paramData.name,
            value: [...normalizedValue],
            valueType: paramData.valueType,
            dataType: paramData.dataType,
            listValues: paramData.listValues,
            default: paramData.default
          });
          initParams.add(initParam);
        });
        
        this.reportParams = restoredParams;
        this.initReportParams = initParams;
        
        // Force re-render to update form elements
        this.paramKey++;
      }

      // Don't mark as pending changes since this is a restore
      this.hasPendingChanges = false;
      
      // Sync parameters with measure definitions after loading from localStorage
      // This ensures missing parameters get default values
      if (this.inventoryMeasureParams && this.inventoryMeasureParams.size() > 0) {
        this.syncParametersWithMeasure();
      }
      
      // Emit the restored settings to parent component
      this.$emit('input', this.reportParams);
      if (this.dateRange.length >= 2) {
        this.$emit('date-range-changed', this.dateRange);
      }
      this.$emit('show-need-reorder-now-changed', this.showNeedReorderNowOnly);
      
    } catch (error) {
      console.error('Failed to load settings from local storage:', error);
    }
  }

  // Clear saved settings from local storage
  private clearLocalStorage() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear settings from local storage:', error);
    }
  }

  private setRestoredDateRange() {
    // Set restored date range directly on DateFilter component if available
    if (this.hasRestoredSettings && this.dateRange.length >= 2) {
      const dateFilter = this.$refs.dateFilter as any;
      if (dateFilter) {
        try {
          // Check if we have valid dates in positions [0] and [1]
          const startDateStr = this.dateRange[0];
          const endDateStr = this.dateRange[1];
          const presetString = this.dateRange.length >= 3 ? this.dateRange[2] : null;
          
          if (startDateStr && endDateStr && startDateStr !== '' && endDateStr !== '') {
            const startDate = new Date(startDateStr);
            const endDate = new Date(endDateStr);
            
            // Only set range if dates are valid
            if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
              dateFilter.range = {
                start: startDate,
                end: endDate
              };
              
              // Use preset string if available, otherwise default to 'custom'
              if (presetString && presetString !== '' && presetString !== 'custom') {
                dateFilter.customDateRange = presetString;
                dateFilter.defaultRange = presetString;
              } else {
                dateFilter.customDateRange = 'custom';
                dateFilter.defaultRange = 'custom';
              }
            }
          }
          
          // Always set the dates array (includes preset string if present)
          dateFilter.dates = [...this.dateRange];
        } catch (error) {
          console.error('Failed to set restored date range:', error);
        }
      }
    }
  }

  private setPresetDateRange(presetRange: string) {
    const dateFilter = this.$refs.dateFilter as any;
    if (dateFilter) {
      try {
        // Set the preset range on DateFilter
        dateFilter.defaultRange = presetRange;
        dateFilter.customDateRange = presetRange;
        
        // Convert the preset to actual dates and set them
        const range = dateFilter.convertTimeRange(presetRange);
        if (range.start && range.end) {
          dateFilter.range = range;
          
          // Update our internal dateRange as well
          const startDate = range.start.format('YYYY-MM-DD');
          const endDate = range.end.format('YYYY-MM-DD');
          this.dateRange = [startDate, endDate, presetRange];
          
          // Emit date range change event and mark as pending change only if not restoring
          if (!this.isRestoring) {
            this.hasPendingChanges = true;
          }
          this.$emit('date-range-changed', this.dateRange);
          // No auto-save - wait for Apply button
        }
      } catch (error) {
        console.error('Failed to set preset date range:', error);
      }
    } else {
      // If DateFilter is not available, we'll set it after next tick
      this.$nextTick(() => {
        this.setPresetDateRange(presetRange);
      });
    }
  }

  private applyDefaultBalancedPreset() {
    // Apply balanced preset as default for new users
    const preset = this.inventoryPresets['balanced'];
    if (!preset) return;

    // Reset all parameters
    this.reportParams = new ParamList();
    this.initReportParams = new ParamList();
    
    // Set showNeedReorderNowOnly to false for all presets
    this.showNeedReorderNowOnly = false;
    
    // Set default date range to last 3 months for balanced preset
    // This will be set after DateFilter is ready
    
    // Apply balanced preset parameters after inventory measure is loaded
    // This will be handled by watching for when inventoryMeasureParams is populated
    this.shouldApplyDefaultPreset = true;
  }

  private applyDefaultPresetParameters() {
    // Apply balanced preset parameters now that inventory measure is loaded
    const preset = this.inventoryPresets['balanced'];
    if (!preset) return;

    // Set showNeedReorderNowOnly to false for all presets
    this.showNeedReorderNowOnly = false;

    for (const presetParam of preset) {
      // Find the measure parameter definition
      const measureParam = this.inventoryMeasureParams.items.find(p => p.code === presetParam.code);
      if (measureParam) {
        const param = new ParamModel({
          code: presetParam.code,
          name: measureParam.name,
          value: presetParam.value,
          valueType: measureParam.valueType,
          dataType: measureParam.dataType,
          listValues: measureParam.listValues
        });
        this.reportParams.add(param);
        this.initReportParams.add(param);
      }
    }
    
    this.paramKey++; // Force re-render
    // Don't call onParamsChange here - this is initialization, not user changes
  }

  // Normalize a raw value against a param's listValues so the v-combobox
  // in ReportParam (which binds item-value="Value") can match it. When the
  // backend returns listValues as [{id, Label, Value}] and a saved setting /
  // default is the numeric `id`, we convert it to the matching `Value`.
  // Free-text values (strings not in the list) are passed through unchanged.
  private resolveListValue(listValues: any, rawValue: any): any {
    if (rawValue === null || rawValue === undefined || !listValues || !listValues.length) {
      return rawValue;
    }
    if (typeof listValues[0] !== 'object' || listValues[0] === null) {
      return rawValue;
    }
    const byValue = listValues.find((item: any) =>
      item && (item.Value === rawValue || item.value === rawValue),
    );
    if (byValue) {
      return rawValue;
    }
    // Loose equality via String() so '68' and 68 both match a numeric id.
    const rawAsString = String(rawValue);
    const byId = listValues.find((item: any) =>
      item && ((item.id !== undefined && String(item.id) === rawAsString)
        || (item.ID !== undefined && String(item.ID) === rawAsString)),
    );
    if (byId) {
      return byId.Value ?? byId.value ?? byId.Label ?? byId.name ?? rawValue;
    }
    return rawValue;
  }

  // For a yes/no toggle served by the backend as listValues, return the Value
  // of the 'No' option (case-insensitive) so we can use it as the safe default
  // — "No" means "don't change behavior", which is what we want for new
  // toggles like "ignore reorder point condition".
  private pickNoOption(listValues: any): string | undefined {
    if (!listValues || !listValues.length) {
      return undefined;
    }
    for (const item of listValues) {
      const raw = typeof item === 'string'
        ? item
        : (item && (item.Value ?? item.value ?? item.Label ?? item.name));
      if (typeof raw === 'string' && raw.toLowerCase() === 'no') {
        return raw;
      }
    }
    return undefined;
  }

  // Sync loaded parameters with measure definitions
  private syncParametersWithMeasure() {
    if (!this.inventoryMeasureParams || this.inventoryMeasureParams.size() === 0) {
      return;
    }
    
    // Always sync parameters with measure definitions, regardless of whether we have existing parameters
    const syncedParams = new ParamList();
    const syncedInitParams = new ParamList();
    
    for (const measureParam of this.inventoryMeasureParams.items) {
      // Find corresponding loaded parameter (if any)
      const loadedParam = this.reportParams && this.reportParams.items ? 
        this.reportParams.items.find(p => p.code === measureParam.code) : null;
      
      
      if (loadedParam) {
        let valueToUse = loadedParam.value;

        // Any list-backed param: if the saved value is a numeric id from the
        // listValues, normalize it to the option's Value so the combobox
        // (item-value="Value") matches instead of rendering the raw id.
        if (measureParam.listValues && measureParam.listValues.length > 0 && Array.isArray(valueToUse)) {
          valueToUse = valueToUse.map((v) => this.resolveListValue(measureParam.listValues, v));
        }

        // Merge loaded values with measure metadata
        const syncedParam = new ParamModel({
          code: measureParam.code,
          name: measureParam.name,
          value: valueToUse, // Use validated value
          valueType: measureParam.valueType,
          dataType: measureParam.dataType,
          listValues: measureParam.listValues, // Use measure's list values
          default: measureParam.default,
          sortOrder: measureParam.sortOrder,
          NeedApiRequest: measureParam.NeedApiRequest
        });
        syncedParams.add(syncedParam);
        
        // Create copy for initParams
        const initParam = new ParamModel({
          code: measureParam.code,
          name: measureParam.name,
          value: Array.isArray(valueToUse) ? [...valueToUse] : valueToUse,
          valueType: measureParam.valueType,
          dataType: measureParam.dataType,
          listValues: measureParam.listValues,
          default: measureParam.default,
          sortOrder: measureParam.sortOrder,
          NeedApiRequest: measureParam.NeedApiRequest
        });
        syncedInitParams.add(initParam);
        
      } else {
        // Parameter wasn't in saved settings, use measure defaults
        let defaultValue = measureParam.default;
        
        // If no default in measure, determine based on parameter type and code
        if (!defaultValue || (Array.isArray(defaultValue) && defaultValue.length === 0)) {
          if (measureParam.code === 'including_preorders' || 
              measureParam.code === 'out_of_stock_days_excluding') {
            
            // Determine what default value to look for based on parameter type
            let targetValue = 'no'; // Default for preorders
            
            // Try to find the target option in listValues
            if (measureParam.listValues && measureParam.listValues.length > 0) {
              const targetOption = measureParam.listValues.find(item => {
                if (typeof item === 'string') {
                  return item.toLowerCase() === targetValue;
                } else if (typeof item === 'object' && item !== null) {
                  // Check all possible properties for the target value (case insensitive)
                  return (
                    item.name === targetValue || item.value === targetValue || item.id === targetValue ||
                    item.name === targetValue.toLowerCase() || item.value === targetValue.toLowerCase() || item.id === targetValue.toLowerCase() ||
                    item.name === targetValue.toUpperCase() || item.value === targetValue.toUpperCase() || item.id === targetValue.toUpperCase() ||
                    item.text === targetValue || item.text === targetValue.toLowerCase() || item.text === targetValue.toUpperCase() ||
                    item.label === targetValue || item.label === targetValue.toLowerCase() || item.label === targetValue.toUpperCase() ||
                    item.Label === targetValue || item.Label === targetValue.toLowerCase() || item.Label === targetValue.toUpperCase() ||
                    item.Value === targetValue || item.Value === targetValue.toLowerCase() || item.Value === targetValue.toUpperCase() ||
                    (item.Value && item.Value.toLowerCase && item.Value.toLowerCase() === targetValue) ||
                    (item.Label && item.Label.toLowerCase && item.Label.toLowerCase() === targetValue)
                  );
                }
                return false;
              });
              
              if (targetOption) {
                const targetOptionValue = typeof targetOption === 'string' ? targetOption : (targetOption.Value || targetOption.value || targetOption.Label || targetOption.name || targetOption.text || targetOption.label || targetOption.id);
                defaultValue = [targetOptionValue];
              } else {
                // Use first option as fallback
                const firstOption = measureParam.listValues[0];
                const firstValue = typeof firstOption === 'string' ? firstOption : (firstOption.Value || firstOption.value || firstOption.Label || firstOption.name || firstOption.id);
                defaultValue = [firstValue];
              }
            } else {
              defaultValue = [targetValue]; // Default to target value string
            }
          } else if (measureParam.code === 'sales_trend') {
            defaultValue = ['1']; // Default to 1.0 (0% adjustment)
          } else if (measureParam.code === 'topn') {
            defaultValue = ['All']; // Default to show all products
          } else if (measureParam.listValues && measureParam.listValues.length > 0) {
            // For yes/no toggles (e.g. "ignore reorder point condition"),
            // default to "No" — it's the safe "don't change behavior" choice.
            // Otherwise fall back to the first item's Value (not .id) so the
            // combobox can match — v-combobox in ReportParam binds
            // item-value="Value", so a numeric id would render as raw text.
            const noValue = this.pickNoOption(measureParam.listValues);
            if (noValue !== undefined) {
              defaultValue = [noValue];
            } else {
              const first = measureParam.listValues[0];
              defaultValue = [typeof first === 'object' && first !== null
                ? (first.Value ?? first.value ?? first.Label ?? first.name ?? first.id)
                : first];
            }
          } else {
            defaultValue = [];
          }
        }

        // Ensure defaultValue is always an array
        if (!Array.isArray(defaultValue)) {
          defaultValue = [defaultValue];
        }

        // Normalize any backend-supplied numeric ids (e.g. measureParam.default
        // = [68]) to the option's Value so the combobox renders "No" / "Yes"
        // instead of showing "68".
        if (measureParam.listValues && measureParam.listValues.length > 0) {
          defaultValue = defaultValue.map((v: any) => this.resolveListValue(measureParam.listValues, v));
        }
        
        const defaultParam = new ParamModel({
          code: measureParam.code,
          name: measureParam.name,
          value: [...defaultValue],
          valueType: measureParam.valueType,
          dataType: measureParam.dataType,
          listValues: measureParam.listValues,
          default: measureParam.default,
          sortOrder: measureParam.sortOrder,
          NeedApiRequest: measureParam.NeedApiRequest
        });
        syncedParams.add(defaultParam);
        
        // Create copy for initParams
        const initParam = new ParamModel({
          code: measureParam.code,
          name: measureParam.name,
          value: [...defaultValue],
          valueType: measureParam.valueType,
          dataType: measureParam.dataType,
          listValues: measureParam.listValues,
          default: measureParam.default,
          sortOrder: measureParam.sortOrder,
          NeedApiRequest: measureParam.NeedApiRequest
        });
        syncedInitParams.add(initParam);
      }
    }
    
    // Update both param lists
    this.reportParams = syncedParams;
    this.initReportParams = syncedInitParams;
    
    // Force re-render to update form elements with proper metadata
    this.paramKey++;
  }

  // Helper methods for preset styling
  private getPresetColor(preset: string): string {
    const colors: { [key: string]: string } = {
      conservative: 'success',
      balanced: 'warning',
      aggressive: 'error', 
      seasonal: 'primary'
    };
    return colors[preset] || 'grey';
  }

  private getPresetIcon(preset: string): string {
    const icons: { [key: string]: string } = {
      conservative: 'mdi-shield-check',
      balanced: 'mdi-scale-balance',
      aggressive: 'mdi-trending-up',
      seasonal: 'mdi-calendar-clock'
    };
    return icons[preset] || 'mdi-tune';
  }

  // Database persistence methods
  private async loadFromDatabase() {
    this.isDatabaseLoading = true;
    try {
      const savedParams = await ReorderUserParamsService.get();
      // Clear the failure flag only AFTER a successful read — resetting it before
      // the await would leave a window where an in-flight retry reads as "not
      // failed" and a concurrent save could slip past the persistence guard.
      this.databaseLoadFailed = false;
      if (savedParams) {
        this.applyDatabaseSettings(savedParams);
        this.hasLoadedFromDatabase = true;

        // Sync parameters with measure definitions after loading
        // This ensures missing parameters get default values
        if (this.inventoryMeasureParams && this.inventoryMeasureParams.size() > 0) {
          this.syncParametersWithMeasure();
        }
      }
      // A null result here is a CONFIRMED empty read (new user), not a failure —
      // the service now throws on network/HTTP errors instead of returning null.
    } catch (error) {
      console.error('Failed to load from database:', error);
      this.databaseSaveError = 'Failed to load saved settings';
      // Mark the failure so mounted() won't fall back to defaults and a later
      // save won't overwrite the unread database settings.
      this.databaseLoadFailed = true;
    } finally {
      this.isDatabaseLoading = false;
    }
  }

  private applyDatabaseSettings(settings: any) {
    if (settings.dateRange && Array.isArray(settings.dateRange)) {
      this.dateRange = settings.dateRange;
      this.hasRestoredSettings = true;
    }
    
    if (typeof settings.showNeedReorderNowOnly === 'boolean') {
      this.showNeedReorderNowOnly = settings.showNeedReorderNowOnly;
    }
    
    if (settings.reportParams && Array.isArray(settings.reportParams)) {
      // Create new ParamLists for both reportParams and initReportParams
      const restoredParams = new ParamList();
      const initParams = new ParamList();
      
      for (const param of settings.reportParams) {
        // Normalize value to always be an array — older saves may have
        // stringified single-select combobox values (no `multiple` attr),
        // which breaks `.every()` checks downstream (e.g. activePreset).
        const normalizedValue = Array.isArray(param.value)
          ? param.value
          : (param.value === null || param.value === undefined ? [] : [param.value]);
        const paramModel = new ParamModel({ ...param, value: [...normalizedValue] });
        restoredParams.add(paramModel);

        // Create a copy for initReportParams
        const initParamModel = new ParamModel({
          code: param.code,
          name: param.name,
          value: [...normalizedValue],
          valueType: param.valueType,
          dataType: param.dataType,
          listValues: param.listValues,
          default: param.default
        });
        initParams.add(initParamModel);
      }
      
      this.reportParams = restoredParams;
      this.initReportParams = initParams;
      
      // Force re-render to update form elements
      this.paramKey++;
    }
    
    // Don't restore expanded state from database - always start collapsed
    this.expanded = false;
  }

  private async saveToDatabase() {
    if (this.saveDebounceTimer) {
      clearTimeout(this.saveDebounceTimer);
    }
    
    this.saveDebounceTimer = setTimeout(async () => {
      await this.performDatabaseSave();
    }, this.saveDebounceMs);
  }

  private async performDatabaseSave(): Promise<boolean> {
    if (this.isSavingToDatabase) return false;

    // Don't persist while a read is in flight or after a failed read: in either
    // state we can't see the stored record, so saving could overwrite it.
    // Persistence resumes only after a successful (re)load (MR !2738). Surface the
    // block as a save error here — this method is the single emitter of
    // database-save-error, so callers don't re-emit it.
    if (!canPersistReorderToDatabase(this.isDatabaseLoading, this.databaseLoadFailed)) {
      this.databaseSaveError = 'Could not save settings — please retry';
      this.$emit('database-save-error', this.databaseSaveError);
      return false;
    }

    this.isSavingToDatabase = true;
    this.databaseSaveError = null;
    
    try {
      const dataToSave = {
        dateRange: this.dateRange,
        showNeedReorderNowOnly: this.showNeedReorderNowOnly,
        reportParams: this.reportParams.items.map(param => ({
          code: param.code,
          name: param.name,
          value: param.value,
          valueType: param.valueType,
          dataType: param.dataType
        })),
        expanded: this.expanded
      };
      
      const success = await ReorderUserParamsService.save(dataToSave);
      
      if (success) {
        this.$emit('database-saved', dataToSave);
        return true;
      } else {
        this.databaseSaveError = 'Failed to save settings';
        this.$emit('database-save-error', 'Save failed');
        return false;
      }
    } catch (error) {
      console.error('Database save error:', error);
      this.databaseSaveError = 'Failed to save settings';
      this.$emit('database-save-error', error);
      return false;
    } finally {
      this.isSavingToDatabase = false;
    }
  }

  private saveSettings() {
    if (this.persistenceMode === 'localStorage') {
      this.saveToLocalStorage();
    } else if (this.persistenceMode === 'database') {
      this.saveToDatabase();
    } else if (this.persistenceMode === 'hybrid') {
      this.saveToLocalStorage();
      this.saveToDatabase();
    }
  }

  private async saveSettingsImmediately(): Promise<boolean> {
    try {
      if (this.persistenceMode === 'localStorage') {
        this.saveToLocalStorage();
        return true;
      } else if (this.persistenceMode === 'database') {
        // Perform database save immediately without debounce
        const success = await this.performDatabaseSave();
        if (!success) {
          // Fallback to localStorage if database save fails
          this.saveToLocalStorage();
        }
        return success;
      } else if (this.persistenceMode === 'hybrid') {
        // Save to both localStorage and database
        this.saveToLocalStorage();
        return await this.performDatabaseSave();
      }
      return true;
    } catch (error) {
      console.error('Failed to save settings immediately:', error);
      // Fallback to localStorage on error
      this.saveToLocalStorage();
      return false;
    }
  }

  beforeDestroy() {
    // Clean up any pending timers
    if (this.saveDebounceTimer) {
      clearTimeout(this.saveDebounceTimer);
    }
    // No auto-save on destroy - only save when user clicks Apply
  }

  public async forceSave(): Promise<boolean> {
    if (this.persistenceMode === 'localStorage') {
      this.saveToLocalStorage();
      return true;
    } else {
      // Propagate the actual persistence result — performDatabaseSave() can now
      // return false (guarded during load / after a failed read), so returning
      // true unconditionally would falsely report success (MR !2738).
      return await this.performDatabaseSave();
    }
  }

  public async resetToDefaults() {
    this.reportParams = new ParamList();
    this.dateRange = [];
    this.showNeedReorderNowOnly = false;
    this.expanded = false;

    // Use the guarded helper so a SecurityError (private mode / iframe) can't
    // reject before the database delete and reset event run.
    this.clearLocalStorage();

    // Skip the DELETE while a read is in flight or after a failed read — issuing
    // a database mutation with no trustworthy view of the record risks clobbering
    // it. The local reset above still applies for the current session (MR !2738).
    if (this.persistenceMode !== 'localStorage'
        && canPersistReorderToDatabase(this.isDatabaseLoading, this.databaseLoadFailed)) {
      await ReorderUserParamsService.delete();
    }

    this.$emit('reset');
  }
}
